import {
  restResourceResolver,
  batchRestResourceResolver,
} from './restResourceResolver/restResourceResolver';
import {
  announcementsResolver,
  dismissAnnouncement,
} from './resolvers/announcements';
import {
  uploadCardCover,
  updateCardDueComplete,
  updateCardCover,
  updateCardList,
  createCardTemplate,
  copyCard,
  deleteCard,
  changeCardDueDate,
} from './resolvers/card';
import {
  addChecklist,
  deleteChecklist,
  updateChecklistName,
  updateChecklistPos,
} from './resolvers/checklist';
import {
  addCheckItem,
  deleteCheckItem,
  updateCheckItemName,
  updateCheckItemPos,
  updateCheckItemState,
} from './resolvers/checkItem';
import { emailProviderResolver } from './resolvers/emailProvider';
import {
  addOneTimeMessagesDismissed,
  updateUserLocale,
  deleteOneTimeMessagesDismissed,
  prepMemberForAtlassianAccountOnboarding,
  prepMemberForEmailHygiene,
  memberAgreementsResolver,
  memberAtlassianAccountsResolver,
  memberAtlassianOrganizationsResolver,
  acceptDeveloperTerms,
  updateMemberAvatarSource,
  uploadMemberAvatar,
  addCampaign,
  updateCampaign,
  resendVerificationEmail,
  changeMemberEmail,
} from './resolvers/member';
import {
  addFreeTrial,
  applyBCDiscount,
  organizationOwnedPluginsResolver,
  organizationStatsResolver,
  organizationCardStatsResolver,
  organizationLabelNamesResolver,
  organizationNewBillableGuestsResolver,
  createOrganization,
  copyBoardToOrg,
  deleteOrganizationLogo,
  addMembersToOrg,
  updateOrganization,
  uploadOrganizationImage,
  organizationCardsResolver,
} from './resolvers/organization';
import {
  salesTaxResolver,
  salesTaxForSubscriberResolver,
  renewalPriceQuotesResolver,
  createBusinessClassPaidAccount,
  createGoldPaidAccount,
  certCaptureTokenResolver,
  activateGoldCredit,
  paidAccountCustomTypeResolverFactory,
  reactivateBusinessClassPaidAccount,
  reactivateGoldPaidAccount,
  updateCreditCardFactory,
  updatePaidProductFactory,
  updateBillingContactDetailsFactory,
  updateInvoiceDetailsFactory,
  cancelPaidAccountFactory,
} from './resolvers/paidAccount';
import {
  publicPluginsResolver,
  pluginCategoriesResolver,
} from './resolvers/publicPlugin';
import { unsplashPhotosResolver } from './resolvers/unsplash';

import {
  enablePlugin,
  updateBoardOrg,
  updateBoardVisibility,
  updateBoardCardCoversPref,
  boardExportResolver,
  startBoardExport,
  statsResolver,
} from './resolvers/board';
import {
  createPlugin,
  deletePlugin,
  updatePlugin,
  createPluginListing,
  updatePluginListing,
  deletePluginListing,
  pluginCollaboratorsResolver,
  addPluginCollaborator,
  removePluginCollaborator,
} from './resolvers/plugin';
import {
  templateCategoriesResolver,
  templateLanguagesResolver,
  templateGalleryResolver,
} from './resolvers/template';
import {
  enterpriseClaimableOrganizationsResolver,
  transferrableDataForOrganizationResolver,
  claimOrganization,
  linkEnterpriseWithAtlassianOrganization,
  managedMembersWithTokensResolver,
  deleteManagedMemberTokens,
  deleteAllManagedMemberTokens,
  updateEnterpriseApiTokenCreationPermission,
} from './resolvers/enterprise';
import { Resolvers } from 'apollo-client';

export const resolvers = {
  Query: {
    announcements: announcementsResolver,
    board: restResourceResolver,
    boards: batchRestResourceResolver,
    card: restResourceResolver,
    cards: batchRestResourceResolver,
    certCaptureToken: certCaptureTokenResolver,
    checklist: restResourceResolver,
    checklists: batchRestResourceResolver,
    list: restResourceResolver,
    lists: batchRestResourceResolver,
    atlassianAccounts: memberAtlassianAccountsResolver,
    emailProvider: emailProviderResolver,
    member: restResourceResolver,
    members: batchRestResourceResolver,
    organization: restResourceResolver,
    organizations: batchRestResourceResolver,
    label: restResourceResolver,
    labels: batchRestResourceResolver,
    enterprise: restResourceResolver,
    enterprises: batchRestResourceResolver,
    publicPlugins: publicPluginsResolver,
    salesTax: salesTaxResolver,
    salesTaxForSubscriber: salesTaxForSubscriberResolver,
    renewalPriceQuotes: renewalPriceQuotesResolver,
    search: restResourceResolver,
    plugin: restResourceResolver,
    plugins: batchRestResourceResolver,
    pluginCategories: pluginCategoriesResolver,
    unsplashPhotos: unsplashPhotosResolver,
    templateCategories: templateCategoriesResolver,
    templateLanguages: templateLanguagesResolver,
    templateGallery: templateGalleryResolver,
  },
  Mutation: {
    addChecklist,
    addOneTimeMessagesDismissed,
    createOrganization,
    copyBoardToOrg,
    updateBoardOrg,
    updateBoardVisibility,
    updateBoardCardCoversPref,
    dismissAnnouncement,
    deleteChecklist,
    updateChecklistName,
    updateChecklistPos,
    addCheckItem,
    deleteCheckItem,
    addCampaign,
    updateCampaign,
    updateCheckItemName,
    updateCheckItemPos,
    updateCheckItemState,
    updateUserLocale,
    updateOrganization,
    prepMemberForAtlassianAccountOnboarding,
    prepMemberForEmailHygiene,
    deleteOneTimeMessagesDismissed,
    createPlugin,
    deletePlugin,
    enablePlugin,
    updatePlugin,
    createPluginListing,
    updatePluginListing,
    deletePluginListing,
    addPluginCollaborator,
    removePluginCollaborator,
    acceptDeveloperTerms,
    updateMemberAvatarSource,
    updateCardCover,
    uploadCardCover,
    updateCardDueComplete,
    updateCardList,
    uploadMemberAvatar,
    changeMemberEmail,
    resendVerificationEmail,
    addMembersToOrg,
    createCardTemplate,
    copyCard,
    deleteCard,
    changeCardDueDate,
    startBoardExport,
    claimOrganization,
    linkEnterpriseWithAtlassianOrganization,
    createBusinessClassPaidAccount,
    createGoldPaidAccount,
    activateGoldCredit,
    reactivateBusinessClassPaidAccount,
    reactivateGoldPaidAccount,
    updateBusinessClassCreditCard: updateCreditCardFactory('organizations'),
    updateGoldCreditCard: updateCreditCardFactory('members'),
    updateBusinessClassPaidProduct: updatePaidProductFactory('organizations'),
    updateGoldPaidProduct: updatePaidProductFactory('members'),
    updateBusinessClassBillingContactDetails: updateBillingContactDetailsFactory(
      'organizations',
    ),
    updateGoldBillingContactDetails: updateBillingContactDetailsFactory(
      'members',
    ),
    cancelBusinessClassPaidAccount: cancelPaidAccountFactory('organizations'),
    cancelGoldPaidAccount: cancelPaidAccountFactory('members'),
    updateBusinessClassBillingInvoiceDetails: updateInvoiceDetailsFactory(
      'organizations',
    ),
    updateGoldBillingInvoiceDetails: updateInvoiceDetailsFactory('members'),
    uploadOrganizationImage,
    deleteOrganizationLogo,
    addFreeTrial,
    applyBCDiscount,
    deleteManagedMemberTokens,
    deleteAllManagedMemberTokens,
    updateEnterpriseApiTokenCreationPermission,
  },

  // Custom type resolvers
  Board: {
    export: boardExportResolver,
    exports: boardExportResolver,
    stats: statsResolver,
  },

  Enterprise: {
    claimableOrganizations: enterpriseClaimableOrganizationsResolver,
    transferrableData: transferrableDataForOrganizationResolver,
    managedMembersWithTokens: managedMembersWithTokensResolver,
  },

  Member: {
    agreements: memberAgreementsResolver,
    atlassianOrganizations: memberAtlassianOrganizationsResolver,
    paidAccount: paidAccountCustomTypeResolverFactory('members'),
  },

  Organization: {
    ownedPlugins: organizationOwnedPluginsResolver,
    stats: organizationStatsResolver,
    newBillableGuests: organizationNewBillableGuestsResolver,
    paidAccount: paidAccountCustomTypeResolverFactory('organizations'),
    cards: organizationCardsResolver,
  },

  Organization_Stats: {
    cards: organizationCardStatsResolver,
    labelNames: organizationLabelNamesResolver,
  },

  Plugin: {
    collaborators: pluginCollaboratorsResolver,
  },
} as Resolvers;
